import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkle, CheckCircle, ArrowRight, Warning, Flower } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const MAX_ATTEMPTS = 6;
const POLL_MS = 2000;

export default function PremiumSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | pending | error
  const [message, setMessage] = useState("Confirming your payment with Stripe…");
  const attempts = useRef(0);
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      setMessage("Missing session, we could not verify a checkout.");
      return;
    }

    const poll = async () => {
      if (stopped.current) return;
      try {
        const { data } = await api.get(`/premium/checkout/status/${sessionId}`);
        if (data.payment_status === "paid" || data.premium) {
          stopped.current = true;
          setState("success");
          setMessage("You're in. Welcome to Wingwomen Premium.");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          stopped.current = true;
          setState("error");
          setMessage("This checkout expired. Please try again.");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          stopped.current = true;
          setState("pending");
          setMessage("Still processing. We'll update your account as soon as Stripe confirms.");
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        stopped.current = true;
        setState("error");
        setMessage(e?.response?.data?.detail || "Could not confirm payment status.");
      }
    };
    poll();
    return () => { stopped.current = true; };
  }, [sessionId, refresh]);

  const Icon = state === "success" ? CheckCircle : state === "error" ? Warning : Sparkle;
  const iconColor =
    state === "success" ? "text-coral-700" : state === "error" ? "text-amber-600" : "text-sage";

  return (
    <div className="min-h-screen bg-cream text-ink grid place-items-center px-6">
      <div className="max-w-lg w-full text-center" data-testid="premium-success-page">
        <div className="inline-flex items-center gap-2 text-coral-700 mb-6">
          <Flower size={22} weight="duotone" />
          <span className="font-display text-xl">Wingwomen</span>
        </div>

        <div className={`mx-auto w-16 h-16 rounded-full bg-white border border-border grid place-items-center shadow-soft ${state === "checking" ? "animate-pulse" : ""}`}>
          <Icon size={28} weight="duotone" className={iconColor} />
        </div>

        <h1 className="font-display text-4xl sm:text-5xl leading-tight mt-6" data-testid="premium-success-title">
          {state === "success" && "Trial started. Welcome to Premium."}
          {state === "checking" && "One moment."}
          {state === "pending" && "Almost there."}
          {state === "error" && "Something's off."}
        </h1>

        <p className="mt-4 text-ink/75 leading-relaxed" data-testid="premium-success-message">
          {message}
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/app/matches"
            data-testid="premium-success-matches"
            className="inline-flex items-center gap-2 bg-coral text-white px-5 py-3 rounded-full text-sm font-medium hover:bg-coral-600 transition-colors"
          >
            See all your matches <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/app"
            data-testid="premium-success-dashboard"
            className="inline-flex items-center gap-2 bg-white border border-border px-5 py-3 rounded-full text-sm hover:border-coral hover:text-coral-700 transition-colors"
          >
            Back to dashboard
          </Link>
        </div>

        {state === "success" && (
          <p className="mt-6 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
            Your 14-day free trial has started. First charge on day 14, cancel any time from Manage Billing.
          </p>
        )}
      </div>
    </div>
  );
}
